import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import type { Json } from "../types/database.types";
import {
  Send,
  Paperclip,
  AtSign,
  X,
  FileText,
  Download,
  Loader2,
  MessageSquare,
  DollarSign,
  LayoutGrid,
  ExternalLink,
  Pin,
  FileBarChart,
  ChevronDown,
  ChevronUp,
  Users,
} from "lucide-react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { useToast } from "../components/ui/ToastModal";
import { logError } from "../lib/logError";
import { AttachmentUploadError, commsAttachmentPath, shortFileName } from "../lib/storageKey";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface Mention {
  type: "variation" | "category" | "subcategory" | "claim_report" | "user";
  id: string;
  label: string;
}

interface Attachment {
  name: string;
  url: string;
  size: number;
  type: string;
}

interface ProjectNote {
  id: string;
  project_id: string;
  user_id: string;
  user_name: string;
  content: string;
  mentions: Mention[];
  attachments: Attachment[];
  created_at: string;
}

interface NoteView {
  user_id: string;
  last_read_at: string;
  user_name: string;
  email: string;
}

interface MentionableItem {
  type: "variation" | "category" | "subcategory" | "claim_report" | "user";
  id: string;
  label: string;
  sublabel?: string;
}

interface PinData {
  id: string;
  note_id: string;
  project_id: string;
  user_id: string;
  user_name: string;
  pinned_at: string;
}

interface PendingFile {
  file: File;
  preview?: string;
}

interface ProjectNotesTabProps {
  projectId: string;
  userId: string;
  onUnreadCountChange?: (count: number) => void;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** Strip mention markers and return plain text truncated to maxLen */
function plainTextPreview(content: string, maxLen = 80): string {
  const plain = content
    .replace(/@\[(\w+):([^:]+):([^\]]+)\]/g, (_m, _t, _i, label) => `@${label}`)
    .replace(/\n+/g, " ")
    .trim();
  return plain.length > maxLen ? plain.slice(0, maxLen - 1) + "…" : plain;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

const URL_REGEX = /(https?:\/\/[^\s<]+)/g;
const MENTION_REGEX = /@\[(\w+):([^:]+):([^\]]+)\]/g;

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

// Mention chip colors
const MENTION_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  variation: { bg: "rgba(99,102,241,0.1)", color: "#6366f1", border: "rgba(99,102,241,0.25)" },
  category: { bg: "rgba(14,165,233,0.1)", color: "#0ea5e9", border: "rgba(14,165,233,0.25)" },
  subcategory: { bg: "rgba(139,92,246,0.1)", color: "#8b5cf6", border: "rgba(139,92,246,0.25)" },
  claim_report: { bg: "rgba(16,185,129,0.1)", color: "#10b981", border: "rgba(16,185,129,0.25)" },
  user: { bg: "rgba(20,184,166,0.1)", color: "#14b8a6", border: "rgba(20,184,166,0.25)" },
};

const MENTION_ICONS: Record<string, typeof DollarSign> = {
  variation: DollarSign,
  category: LayoutGrid,
  subcategory: FileText,
  claim_report: FileBarChart,
  user: Users,
};

const MONTH_SHORT = [
  "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// ─────────────────────────────────────────────
// Smart Content Renderer
// ─────────────────────────────────────────────
/**
 * Format variation label for display: "Variation Docket #N" where N is the trailing number.
 * e.g. "Acme Construction-Springfield Lakes - Multispan-Foreman-SF-3NT4X-1" → "Variation Docket #1"
 */
function formatVariationLabel(rawLabel: string): string {
  // Extract the last number/digit segment from the docket string
  const trailingMatch = rawLabel.match(/[-#]?(\d+)\s*$/);
  if (trailingMatch) {
    return `Variation Docket #${trailingMatch[1]}`;
  }
  // Fallback: if docket has no trailing number, show first 20 chars
  return rawLabel.length > 25 ? `Variation ${rawLabel.slice(0, 22)}…` : `Variation ${rawLabel}`;
}

function renderSmartContent(
  content: string,
  onMentionClick: (type: string, id: string) => void
) {
  // First split by mention markers, then handle URLs in text parts
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const mentionRegex = new RegExp(MENTION_REGEX.source, "g");

  while ((match = mentionRegex.exec(content)) !== null) {
    // Add text before mention (with URL handling)
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index);
      parts.push(...renderTextWithUrls(textBefore, parts.length));
    }

    // Add mention chip
    const [, type, id, label] = match;
    const style = MENTION_STYLES[type] || MENTION_STYLES.variation;
    const Icon = MENTION_ICONS[type] || DollarSign;

    // Use friendly label for variations ("Variation Docket #N" instead of full docket string)
    const displayLabel =
      type === "variation"
        ? formatVariationLabel(label)
        : type === "user"
          ? `@${label}`
          : label;

    parts.push(
      <span
        key={`mention-${parts.length}`}
        onClick={(e) => {
          e.stopPropagation();
          onMentionClick(type, id);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          padding: "1px 8px",
          borderRadius: "4px",
          background: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          fontSize: "0.78rem",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.15s ease",
          verticalAlign: "middle",
          lineHeight: 1.6,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.filter = "brightness(0.92)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.filter = "none";
          e.currentTarget.style.transform = "none";
        }}
        title={type === "user" ? `Team member: ${label}` : `Go to ${type}: ${label}`}
      >
        <Icon size={11} />
        {displayLabel}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(...renderTextWithUrls(content.slice(lastIndex), parts.length));
  }

  return parts;
}

function renderTextWithUrls(text: string, keyOffset: number): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let urlMatch: RegExpExecArray | null;
  const urlRegex = new RegExp(URL_REGEX.source, "g");

  while ((urlMatch = urlRegex.exec(text)) !== null) {
    if (urlMatch.index > lastIdx) {
      parts.push(text.slice(lastIdx, urlMatch.index));
    }
    const url = urlMatch[1];
    const displayUrl = url.length > 45 ? url.slice(0, 42) + "..." : url;
    parts.push(
      <a
        key={`url-${keyOffset}-${parts.length}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#6366f1",
          textDecoration: "none",
          borderBottom: "1px dashed rgba(99,102,241,0.4)",
          fontSize: "inherit",
          wordBreak: "break-all",
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderBottomStyle = "solid";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderBottomStyle = "dashed";
        }}
      >
        {displayUrl}
        <ExternalLink size={11} style={{ flexShrink: 0, opacity: 0.6 }} />
      </a>
    );
    lastIdx = urlMatch.index + urlMatch[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(text.slice(lastIdx));
  }
  return parts;
}

// ─────────────────────────────────────────────
// ProjectNotesTab Component
// ─────────────────────────────────────────────
export function ProjectNotesTab({
  projectId,
  userId,
  onUnreadCountChange,
}: ProjectNotesTabProps) {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [views, setViews] = useState<NoteView[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [text, setText] = useState("");
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Unread message tracking: stored BEFORE markAsRead updates it
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);

  // Mention system
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionItems, setMentionItems] = useState<MentionableItem[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [, setMentionStartPos] = useState<number | null>(null);

  // Pin system
  const [pinnedNotes, setPinnedNotes] = useState<PinData[]>([]);
  const [showPinned, setShowPinned] = useState(true);
  const [highlightedNoteId, setHighlightedNoteId] = useState<string | null>(null);

  // Image lightbox
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string } | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const mentionAnchorRange = useRef<Range | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unreadDividerRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  // Scroll to a specific note and flash-highlight it
  const scrollToNote = useCallback((noteId: string) => {
    const el = feedRef.current?.querySelector(`[data-note-id="${noteId}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedNoteId(noteId);
      setTimeout(() => setHighlightedNoteId(null), 2000);
    }
  }, []);

  // ─────────────────────────────
  // Load mentionable entities
  // ─────────────────────────────
  const loadMentionables = useCallback(async () => {
    const [variationsRes, categoriesRes, subcategoriesRes, reportsRes] = await Promise.all([
      supabase
        .from("claims_variations")
        .select("id, docket_no, title")
        .eq("project_id", projectId)
        .order("docket_no"),
      supabase
        .from("claims_categories")
        .select("id, name")
        .eq("project_id", projectId)
        .order("name"),
      supabase
        .from("claims_subcategories")
        .select("id, name, category_id")
        .order("name"),
      supabase
        .from("claims_reports")
        .select("id, claim_number, claim_month, claim_year")
        .eq("project_id", projectId)
        .order("claim_year", { ascending: false }),
    ]);

    const items: MentionableItem[] = [];

    // Variations
    (variationsRes.data || []).forEach((v) => {
      items.push({
        type: "variation",
        id: v.id,
        label: v.docket_no || v.title || "Untitled",
        sublabel: v.title || undefined,
      });
    });

    // Categories
    const categories = categoriesRes.data || [];
    categories.forEach((c) => {
      items.push({
        type: "category",
        id: c.id,
        label: c.name,
      });
    });

    // Subcategories (filter to only those belonging to this project's categories)
    const catIds = new Set(categories.map((c) => c.id));
    (subcategoriesRes.data || []).forEach((s) => {
      if (catIds.has(s.category_id)) {
        const parentCat = categories.find((c) => c.id === s.category_id);
        items.push({
          type: "subcategory",
          id: s.id,
          label: s.name,
          sublabel: parentCat?.name,
        });
      }
    });

    // Claim Reports: format as "Claim Report Period Feb-26"
    (reportsRes.data || []).forEach((r) => {
      const monthName = MONTH_SHORT[r.claim_month] || "";
      const yearShort = String(r.claim_year).slice(-2);
      const periodLabel = `Claim Report Period ${monthName}-${yearShort}`;
      items.push({
        type: "claim_report",
        id: r.id,
        label: periodLabel,
        sublabel: `Claim #${r.claim_number}`,
      });
    });

    // Team Members
    const orgId = requireOrganizationId();
    const { data: membersData } = await supabase
      .from("organization_members")
      .select(`
        user_id,
        role,
        profiles!organization_members_user_id_profiles_fkey (
          full_name,
          email
        )
      `)
      .eq("organization_id", orgId)
      .order("joined_at", { ascending: true });

    const seenUserIds = new Set<string>();
    const userItems: MentionableItem[] = [];
    (membersData || []).forEach((row: Record<string, unknown>) => {
      const uid = row.user_id as string;
      if (seenUserIds.has(uid)) return;
      seenUserIds.add(uid);
      const profile = row.profiles as Record<string, unknown> | null;
      if (profile?.full_name) {
        userItems.push({
          type: "user",
          id: uid,
          label: profile.full_name as string,
          sublabel: (profile.email as string) || undefined,
        });
      }
    });
    // Sort team members alphabetically
    userItems.sort((a, b) => a.label.localeCompare(b.label));
    items.push(...userItems);

    setMentionItems(items);
  }, [projectId]);

  // ─────────────────────────────
  // Fetch notes + views
  // ─────────────────────────────
  const fetchNotes = useCallback(async () => {
    const { data: notesData } = await supabase
      .from("claims_project_notes")
      .select("*")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });

    setNotes(((notesData || []) as unknown as ProjectNote[]));

    // Fetch views
    const { data: viewsData } = await supabase
      .from("claims_project_note_views")
      .select("user_id, last_read_at")
      .eq("project_id", projectId);

    if (viewsData && viewsData.length > 0) {
      const userIds = viewsData.map((v) => v.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);

      const enhancedViews = viewsData.map((v) => {
        const profile = profiles?.find((p) => p.id === v.user_id);
        return {
          user_id: v.user_id,
          last_read_at: v.last_read_at,
          user_name: profile?.full_name || "Unknown",
          email: profile?.email || "",
        };
      });
      setViews(enhancedViews);
    } else {
      setViews([]);
    }

    setLoading(false);
  }, [projectId]);

  // ─────────────────────────────
  // Mark as read
  // ─────────────────────────────
  const markAsRead = useCallback(async () => {
    if (!userId || !projectId) return;
    await supabase.from("claims_project_note_views").upsert(
      {
        organization_id: requireOrganizationId(),
        project_id: projectId,
        user_id: userId,
        last_read_at: new Date().toISOString(),
      },
      { onConflict: "project_id, user_id" }
    );
    onUnreadCountChange?.(0);
  }, [userId, projectId, onUnreadCountChange]);

  // ─────────────────────────────
  // Fetch pinned notes
  // ─────────────────────────────
  const fetchPins = useCallback(async () => {
    const { data } = await supabase
      .from("claims_project_note_pins")
      .select("*")
      .eq("project_id", projectId)
      .order("pinned_at", { ascending: false });
    setPinnedNotes((data || []) as unknown as PinData[]);
  }, [projectId]);

  // ─────────────────────────────
  // Pin / Unpin handlers
  // ─────────────────────────────
  const handlePin = useCallback(async (note: ProjectNote) => {
    // Get user name
    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name")
      .eq("id", userId)
      .single();
    const userName = profile?.full_name || "Unknown";

    const { error } = await supabase
      .from("claims_project_note_pins")
      .insert({
        organization_id: requireOrganizationId(),
        note_id: note.id,
        project_id: projectId,
        user_id: userId,
        user_name: userName,
      });

    if (error) {
      if (error.code === "23505") {
        addToast({ type: "info", title: "Already pinned" });
      } else {
        addToast({ type: "error", title: "Failed to pin" });
      }
      return;
    }

    addToast({ type: "success", title: "Message pinned 📌" });
    fetchPins();

    // Notify via edge function (fire-and-forget)
    const localTime = new Date().toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    supabase.functions
      .invoke("notify-project-note", {
        body: {
          noteId: note.id,
          originUrl: window.location.origin,
          localTime,
          type: "pinned_note",
          pinnedBy: userName,
        },
      })
      .catch((err) => logError("Failed to notify pin:", err));
  }, [userId, projectId, addToast, fetchPins]);

  const handleUnpin = useCallback(async (noteId: string) => {
    const { error } = await supabase
      .from("claims_project_note_pins")
      .delete()
      .eq("note_id", noteId)
      .eq("user_id", userId);

    if (error) {
      addToast({ type: "error", title: "Failed to unpin" });
      return;
    }
    addToast({ type: "success", title: "Message unpinned" });
    fetchPins();
  }, [userId, addToast, fetchPins]);

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightboxImage) return;
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [lightboxImage]);

  // ─────────────────────────────
  // Init
  // ─────────────────────────────
  useEffect(() => {
    const init = async () => {
      // Fetch the user's last_read_at BEFORE marking as read (for unread divider)
      const { data: viewData } = await supabase
        .from("claims_project_note_views")
        .select("last_read_at")
        .eq("project_id", projectId)
        .eq("user_id", userId)
        .maybeSingle();
      setLastReadAt(viewData?.last_read_at || null);

      await markAsRead();
      await fetchNotes();
      await fetchPins();
    };
    init();
    loadMentionables();
  }, [fetchNotes, markAsRead, loadMentionables, fetchPins, projectId, userId]);

  // ─────────────────────────────
  // Realtime subscription
  // ─────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel(`project-notes-${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "claims_project_notes",
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          // Any change (insert/update/delete), refetch notes
          if (payload.eventType === "INSERT") {
            const newNote = payload.new as { user_id: string };
            if (newNote.user_id !== userId) {
              // Another user posted, also mark as read
              markAsRead();
            }
          }
          fetchNotes();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "claims_project_note_views",
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          // Someone viewed, refresh views (read receipts)
          fetchNotes();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "claims_project_note_pins",
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          fetchPins();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, userId, fetchNotes, markAsRead, fetchPins]);

  // Auto-scroll: on first load, scroll to unread divider; on new messages, scroll to bottom
  useEffect(() => {
    if (!feedRef.current || notes.length === 0) return;

    if (!hasInitiallyScrolled) {
      // First load: scroll to unread divider if it exists, otherwise bottom
      if (unreadDividerRef.current) {
        unreadDividerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        feedRef.current.scrollTop = feedRef.current.scrollHeight;
      }
      setHasInitiallyScrolled(true);
    } else {
      // Subsequent updates (new messages): scroll to bottom
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  // ─────────────────────────────
  // Mention filtering
  // ─────────────────────────────
  const filteredMentions = useMemo(() => {
    if (!showMentions) return [];
    const q = mentionQuery.toLowerCase();
    return mentionItems
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          (item.sublabel && item.sublabel.toLowerCase().includes(q))
      )
      .slice(0, 12);
  }, [showMentions, mentionQuery, mentionItems]);

  // ─────────────────────────────
  // ContentEditable Rich Editor Helpers
  // ─────────────────────────────

  /** Walk the editor DOM and serialize to plain text with @[type:id:label] markup */
  const serializeEditor = (): string => {
    const editor = editorRef.current;
    if (!editor) return "";

    const walk = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || "";
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        // Mention chip span
        if (el.dataset.mentionType) {
          return `@[${el.dataset.mentionType}:${el.dataset.mentionId}:${el.dataset.mentionLabel}]`;
        }
        // <br> → newline
        if (el.tagName === "BR") return "\n";
        // Recurse into other elements (div wraps = newlines in contentEditable)
        let result = "";
        el.childNodes.forEach((child) => {
          result += walk(child);
        });
        // Block elements like <div> insert a newline before their content (contentEditable behavior)
        if (el.tagName === "DIV" && el !== editor && el.previousSibling) {
          result = "\n" + result;
        }
        return result;
      }
      return "";
    };

    let result = "";
    editor.childNodes.forEach((child) => {
      result += walk(child);
    });
    return result;
  };



  /** Update the `text` state from editor content (for submit button disabled state) */
  const syncTextState = () => {
    setText(serializeEditor());
  };

  // Textarea handling → now contentEditable input handler
  // ─────────────────────────────
  const handleEditorInput = () => {
    syncTextState();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      setShowMentions(false);
      setMentionStartPos(null);
      return;
    }

    const range = sel.getRangeAt(0);
    const container = range.startContainer;

    // Only detect @ in text nodes
    if (container.nodeType !== Node.TEXT_NODE) {
      setShowMentions(false);
      setMentionStartPos(null);
      return;
    }

    const textContent = container.textContent || "";
    const cursorOffset = range.startOffset;
    const textBeforeCursor = textContent.slice(0, cursorOffset);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " ";
      if (charBeforeAt === " " || charBeforeAt === "\n" || charBeforeAt === "\u00A0" || lastAtIndex === 0) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
        const q = textAfterAt.toLowerCase();
        const hasMatches = mentionItems.some(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            (item.sublabel && item.sublabel.toLowerCase().includes(q))
        );
        if (hasMatches || textAfterAt.length === 0) {
          // Save the Range so we know where to insert the chip
          const anchorRange = document.createRange();
          anchorRange.setStart(container, lastAtIndex);
          anchorRange.setEnd(container, cursorOffset);
          mentionAnchorRange.current = anchorRange;

          setShowMentions(true);
          setMentionQuery(textAfterAt);
          setMentionStartPos(lastAtIndex);
          setMentionIndex(0);
          return;
        }
      }
    }

    setShowMentions(false);
    setMentionStartPos(null);
  };

  const handleMentionSelect = (item: MentionableItem) => {
    const editor = editorRef.current;
    const anchor = mentionAnchorRange.current;
    if (!editor || !anchor) return;

    const style = MENTION_STYLES[item.type] || MENTION_STYLES.variation;

    // Display label (friendly version for variations)
    const displayLabel =
      item.type === "variation"
        ? formatVariationLabel(item.label)
        : item.type === "user"
          ? `@${item.label}`
          : item.label;

    // Create the chip span
    const chip = document.createElement("span");
    chip.contentEditable = "false";
    chip.dataset.mentionType = item.type;
    chip.dataset.mentionId = item.id;
    chip.dataset.mentionLabel = item.label;
    chip.setAttribute("style", [
      "display:inline-flex",
      "align-items:center",
      "gap:3px",
      "padding:1px 8px",
      "border-radius:4px",
      `background:${style.bg}`,
      `color:${style.color}`,
      `border:1px solid ${style.border}`,
      "font-size:0.78rem",
      "font-weight:600",
      "cursor:default",
      "vertical-align:middle",
      "line-height:1.6",
      "user-select:none",
      "margin:0 1px",
    ].join(";"));

    // Icon SVG
    const iconSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    iconSvg.setAttribute("width", "11");
    iconSvg.setAttribute("height", "11");
    iconSvg.setAttribute("viewBox", "0 0 24 24");
    iconSvg.setAttribute("fill", "none");
    iconSvg.setAttribute("stroke", "currentColor");
    iconSvg.setAttribute("stroke-width", "2");
    iconSvg.setAttribute("stroke-linecap", "round");
    iconSvg.setAttribute("stroke-linejoin", "round");

    // Use the correct icon path based on type
    const iconPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
    if (item.type === "variation") {
      iconPath.setAttribute("d", "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"); // DollarSign
    } else if (item.type === "category") {
      // LayoutGrid
      iconPath.setAttribute("d", "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z");
    } else if (item.type === "claim_report") {
      // FileBarChart
      iconPath.setAttribute("d", "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z");
      const line1 = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line1.setAttribute("x1", "12"); line1.setAttribute("y1", "18");
      line1.setAttribute("x2", "12"); line1.setAttribute("y2", "12");
      iconSvg.appendChild(line1);
      const line2 = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line2.setAttribute("x1", "8"); line2.setAttribute("y1", "18");
      line2.setAttribute("x2", "8"); line2.setAttribute("y2", "15");
      iconSvg.appendChild(line2);
      const line3 = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line3.setAttribute("x1", "16"); line3.setAttribute("y1", "18");
      line3.setAttribute("x2", "16"); line3.setAttribute("y2", "13");
      iconSvg.appendChild(line3);
    } else if (item.type === "user") {
      // Users icon (two people)
      iconPath.setAttribute("d", "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2");
      const circle1 = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle1.setAttribute("cx", "9"); circle1.setAttribute("cy", "7"); circle1.setAttribute("r", "4");
      iconSvg.appendChild(circle1);
      const path2 = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path2.setAttribute("d", "M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75");
      iconSvg.appendChild(path2);
    } else {
      // FileText (subcategory)
      iconPath.setAttribute("d", "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z");
    }
    iconSvg.appendChild(iconPath);
    chip.appendChild(iconSvg);

    // Label text
    const labelText = document.createTextNode(displayLabel);
    chip.appendChild(labelText);

    // Delete the "@query" text and insert the chip
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(anchor);
      anchor.deleteContents();
      anchor.insertNode(chip);

      // Insert a space after the chip and move cursor there
      const space = document.createTextNode("\u00A0");
      chip.after(space);

      // Move cursor after the space
      const newRange = document.createRange();
      newRange.setStartAfter(space);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }

    mentionAnchorRange.current = null;
    setShowMentions(false);
    setMentionStartPos(null);
    syncTextState();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showMentions && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((prev) => Math.min(prev + 1, filteredMentions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        handleMentionSelect(filteredMentions[mentionIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowMentions(false);
      }
    }
  };

  // ─────────────────────────────
  // File handling
  // ─────────────────────────────
  const addFiles = (fileList: FileList) => {
    const newFiles: PendingFile[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        addToast({ type: "error", title: `Unsupported file type: ${file.name}` });
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        addToast({ type: "error", title: `File too large (max 10MB): ${file.name}` });
        continue;
      }
      if (pendingFiles.length + newFiles.length >= MAX_FILES) {
        addToast({ type: "error", title: `Max ${MAX_FILES} files per note` });
        break;
      }
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined;
      newFiles.push({ file, preview });
    }
    setPendingFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => {
      const next = [...prev];
      if (next[index].preview) URL.revokeObjectURL(next[index].preview!);
      next.splice(index, 1);
      return next;
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  // Clipboard paste → auto-attach images (messenger-style)
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          // Create a named file (clipboard gives generic "image.png")
          const ext = file.type.split("/")[1] || "png";
          const namedFile = new File(
            [file],
            `pasted-image-${Date.now()}.${ext}`,
            { type: file.type }
          );
          imageFiles.push(namedFile);
        }
      }
    }

    if (imageFiles.length > 0) {
      // Prevent the default paste behavior for images (no base64 text)
      e.preventDefault();
      // Create a synthetic FileList-like object
      const dt = new DataTransfer();
      imageFiles.forEach((f) => dt.items.add(f));
      addFiles(dt.files);
      addToast({ type: "success", title: `${imageFiles.length} image${imageFiles.length > 1 ? "s" : ""} pasted` });
    } else {
      // For text paste, strip formatting, plain text only
      e.preventDefault();
      const plainText = e.clipboardData.getData("text/plain");
      if (plainText) {
        document.execCommand("insertText", false, plainText);
      }
    }
  };

  // ─────────────────────────────
  // Submit note
  // ─────────────────────────────
  const handleSubmit = async () => {
    const contentText = serializeEditor();
    if ((!contentText.trim() && pendingFiles.length === 0) || submitting) return;
    setSubmitting(true);
    setUploading(pendingFiles.length > 0);

    try {
      // Get user profile for name
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", userId)
        .single();

      const userName = profile?.full_name || "Unknown";

      // Extract structured mentions from content
      const mentions: Mention[] = [];
      const mentionRegex = new RegExp(MENTION_REGEX.source, "g");
      let m: RegExpExecArray | null;
      while ((m = mentionRegex.exec(contentText)) !== null) {
        mentions.push({
          type: m[1] as Mention["type"],
          id: m[2],
          label: m[3],
        });
      }

      // Generate a note ID upfront so we can upload files first
      const noteId = crypto.randomUUID();

      // Upload attachments FIRST (before insert) using the pre-generated noteId
      let attachments: Attachment[] = [];
      if (pendingFiles.length > 0) {
        attachments = await Promise.all(
          pendingFiles.map(async (pf, index) => {
            const filePath = commsAttachmentPath(projectId, noteId, Date.now(), index, pf.file.name);
            const { error: uploadErr } = await supabase.storage
              .from("project-note-attachments")
              .upload(filePath, pf.file);

            if (uploadErr) {
              throw new AttachmentUploadError(pf.file.name, uploadErr);
            }

            // For private bucket, use createSignedUrl
            const { data: signedData } = await supabase.storage
              .from("project-note-attachments")
              .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year

            const { data: urlData } = supabase.storage
              .from("project-note-attachments")
              .getPublicUrl(filePath);

            return {
              name: pf.file.name,
              url: signedData?.signedUrl || urlData.publicUrl,
              size: pf.file.size,
              type: pf.file.type,
            };
          })
        );
      }

      // Single atomic INSERT with attachments already populated
       
      const { data: newNote, error: insertError } = await supabase
        .from("claims_project_notes")
        .insert({
          organization_id: requireOrganizationId(),
          id: noteId,
          project_id: projectId,
          user_id: userId,
          user_name: userName,
          content: contentText.trim(),
          mentions: mentions as unknown as Json,
          attachments: attachments as unknown as Json,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Notify team via Edge Function (fire-and-forget)
      if (newNote) {
        const localTime = new Date().toLocaleString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        supabase.functions
          .invoke("notify-project-note", {
            body: {
              noteId: newNote.id,
              originUrl: window.location.origin,
              localTime,
              type: "new_note",
            },
          })
          .catch((err) => {
            logError("Failed to notify team:", err);
          });
      }

      // Reset editor
      if (editorRef.current) editorRef.current.innerHTML = "";
      setText("");
      pendingFiles.forEach((pf) => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });
      setPendingFiles([]);

      // Update last_read_at (posting means you've seen everything)
      await markAsRead();

      addToast({ type: "success", title: "Message sent" });
      fetchNotes();
    } catch (err) {
      logError("Failed to send message:", err);
      addToast({
        type: "error",
        title:
          err instanceof AttachmentUploadError
            ? `Upload failed for "${shortFileName(err.fileName)}". Your message was not sent.`
            : "Failed to send message",
      });
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  // ─────────────────────────────
  // Mention click navigation
  // ─────────────────────────────
  const handleMentionClick = useCallback(
    (type: string, id: string) => {
      const currentUrl = new URL(window.location.href);
      if (type === "variation") {
        // Smart route: open Variation Detail Modal (not comments)
        currentUrl.searchParams.set("tab", "variations");
        currentUrl.searchParams.set("openVariationDetail", id);
      } else if (type === "category") {
        // Smart route: switch to claims → range view → scroll to category
        currentUrl.searchParams.set("tab", "claims");
        currentUrl.searchParams.set("claimsView", "range");
        currentUrl.searchParams.set("highlightCategory", id);
      } else if (type === "subcategory") {
        // Smart route: switch to claims → range view → scroll to subcategory
        currentUrl.searchParams.set("tab", "claims");
        currentUrl.searchParams.set("claimsView", "range");
        currentUrl.searchParams.set("openSubcategory", id);
      } else if (type === "claim_report") {
        // Smart route: switch to reports tab and highlight specific report
        currentUrl.searchParams.set("tab", "reports");
        currentUrl.searchParams.set("highlightReport", id);
      } else if (type === "user") {
        // User mentions are non-navigable (no destination page)
        return;
      }
      window.location.href = currentUrl.toString();
    },
    []
  );

  // ─────────────────────────────
  // Display text with rendered mentions (in the textarea preview)
  // ─────────────────────────────

  // ─────────────────────────────
  // Render
  // ─────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "64px 0",
          color: "var(--text-muted)",
          gap: "8px",
        }}
      >
        <Loader2 size={18} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
        Loading notes...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 280px)",
        minHeight: "500px",
        background: "var(--bg-primary)",
        borderRadius: "12px",
        border: "1px solid var(--border-color-light)",
        overflow: "hidden",
        marginBottom: "72px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          borderBottom: "1px solid var(--border-color-light)",
          background: "var(--bg-surface)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <MessageSquare size={18} style={{ color: "#6366f1" }} />
          <h3
            style={{
              margin: 0,
              fontSize: "0.95rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            Project Comms
          </h3>
          <span
            style={{
              fontSize: "0.72rem",
              color: "var(--text-muted)",
              background: "var(--bg-secondary, rgba(0,0,0,0.04))",
              padding: "2px 8px",
              borderRadius: "10px",
              fontWeight: 500,
            }}
          >
            {notes.length} {notes.length === 1 ? "message" : "messages"}
          </span>
        </div>
        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
          Use <code style={{ background: "rgba(0,0,0,0.06)", padding: "1px 4px", borderRadius: "3px", fontSize: "0.68rem" }}>@</code> to reference variations, categories, subcategories, or claim reports
        </div>
      </div>

      {/* Pinned Notes Section */}
      {pinnedNotes.length > 0 && (
        <div
          style={{
            borderBottom: "1px solid var(--border-color-light)",
            background: "linear-gradient(180deg, rgba(245, 158, 11, 0.04) 0%, transparent 100%)",
          }}
        >
          <button
            onClick={() => setShowPinned(!showPinned)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "100%",
              padding: "8px 20px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#f59e0b",
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.01em",
            }}
          >
            <Pin size={11} />
            {pinnedNotes.length} Pinned
            {showPinned ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {showPinned && (
            <div style={{ padding: "0 16px 10px", display: "flex", flexDirection: "column", gap: "4px" }}>
              {pinnedNotes.map((pin) => {
                const pinnedNote = notes.find((n) => n.id === pin.note_id);
                if (!pinnedNote) return null;
                const preview = plainTextPreview(pinnedNote.content, 90);
                const hasAttachments = (pinnedNote.attachments || []).length > 0;

                return (
                  <div
                    key={pin.id}
                    onClick={() => scrollToNote(pin.note_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") scrollToNote(pin.note_id); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      background: "rgba(245, 158, 11, 0.05)",
                      border: "1px solid rgba(245, 158, 11, 0.12)",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(245, 158, 11, 0.10)";
                      e.currentTarget.style.borderColor = "rgba(245, 158, 11, 0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(245, 158, 11, 0.05)";
                      e.currentTarget.style.borderColor = "rgba(245, 158, 11, 0.12)";
                    }}
                  >
                    <Pin size={10} style={{ color: "#f59e0b", flexShrink: 0 }} />
                    <span style={{
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      color: "var(--text-primary)",
                      flexShrink: 0,
                    }}>
                      {pinnedNote.user_name.split(" ")[0]}:
                    </span>
                    <span style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "0.72rem",
                      color: "var(--text-secondary)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      {preview}
                    </span>
                    {hasAttachments && (
                      <Paperclip size={10} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                    )}
                    <span style={{
                      fontSize: "0.62rem",
                      color: "var(--text-muted)",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}>
                      {timeAgo(pinnedNote.created_at)}
                    </span>
                    {pin.user_id === userId && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUnpin(pin.note_id); }}
                        title="Unpin"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--text-muted)",
                          padding: "2px",
                          borderRadius: "4px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Comms Feed */}
      <div
        ref={feedRef}
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {notes.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "var(--text-muted)",
              padding: "64px 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <MessageSquare size={40} style={{ opacity: 0.2 }} />
            <p style={{ margin: 0, fontSize: "0.88rem" }}>
              No messages yet. Start the conversation below.
            </p>
          </div>
        ) : (
          notes.map((note, noteIndex) => {
            // Seen-by computation (Messenger style)
            const otherViews = views.filter((v) => v.user_id !== userId);
            const seenByHere = otherViews.filter((v) => {
              const lastRead = new Date(v.last_read_at).getTime();
              const thisTime = new Date(note.created_at).getTime();
              if (thisTime > lastRead) return false;
              const nextNote = notes[noteIndex + 1];
              if (nextNote) {
                const nextTime = new Date(nextNote.created_at).getTime();
                if (nextTime <= lastRead) return false;
              }
              return true;
            });

            const isOwnNote = note.user_id === userId;

            // Determine if this is the first unread message (for "New messages" divider)
            const isFirstUnread = (() => {
              if (!lastReadAt) return noteIndex === 0; // Never visited = all are new
              const noteTime = new Date(note.created_at).getTime();
              const readTime = new Date(lastReadAt).getTime();
              if (noteTime <= readTime) return false;
              // Check if previous note was read (or this is the first note)
              if (noteIndex === 0) return true;
              const prevNoteTime = new Date(notes[noteIndex - 1].created_at).getTime();
              return prevNoteTime <= readTime;
            })();

            return (
              <div key={note.id}>
                {/* Unread messages divider */}
                {isFirstUnread && (
                  <div
                    ref={unreadDividerRef}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      margin: "8px 0 12px",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        background: "linear-gradient(to right, transparent, #f59e0b, #f59e0b)",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "#f59e0b",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        whiteSpace: "nowrap",
                        padding: "2px 10px",
                        background: "rgba(245, 158, 11, 0.08)",
                        borderRadius: "10px",
                        border: "1px solid rgba(245, 158, 11, 0.2)",
                      }}
                    >
                      New messages
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: "1px",
                        background: "linear-gradient(to left, transparent, #f59e0b, #f59e0b)",
                      }}
                    />
                  </div>
                )}
                {/* Note card */}
                <div
                  data-note-id={note.id}
                  style={{
                    display: "flex",
                    gap: "10px",
                    padding: "12px 14px",
                    background: highlightedNoteId === note.id
                      ? "rgba(245, 158, 11, 0.12)"
                      : isOwnNote
                        ? "rgba(99,102,241,0.04)"
                        : "rgba(34,197,94,0.04)",
                    borderRadius: "10px",
                    borderLeft: `3px solid ${highlightedNoteId === note.id ? "#f59e0b" : isOwnNote ? "#6366f1" : "#22c55e"}`,
                    transition: "all 0.4s ease",
                    boxShadow: highlightedNoteId === note.id
                      ? "0 0 0 2px rgba(245, 158, 11, 0.3), 0 2px 12px rgba(245, 158, 11, 0.1)"
                      : "none",
                  }}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: isOwnNote
                        ? "linear-gradient(135deg, #a5b4fc, #6366f1)"
                        : "linear-gradient(135deg, #86efac, #22c55e)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      flexShrink: 0,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {getInitials(note.user_name)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          color: "var(--text-primary)",
                        }}
                      >
                        {note.user_name.split(" ")[0]}
                      </span>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {timeAgo(note.created_at)}
                      </span>
                      {/* Pin indicator */}
                      {pinnedNotes.some((p) => p.note_id === note.id) && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "2px",
                            fontSize: "0.62rem",
                            fontWeight: 600,
                            color: "#f59e0b",
                            background: "rgba(245, 158, 11, 0.08)",
                            padding: "1px 6px",
                            borderRadius: "8px",
                            border: "1px solid rgba(245, 158, 11, 0.2)",
                          }}
                        >
                          <Pin size={9} />
                          Pinned
                        </span>
                      )}
                      {/* Pin/Unpin action button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const existingPin = pinnedNotes.find((p) => p.note_id === note.id && p.user_id === userId);
                          if (existingPin) {
                            handleUnpin(note.id);
                          } else {
                            handlePin(note);
                          }
                        }}
                        title={pinnedNotes.some((p) => p.note_id === note.id && p.user_id === userId) ? "Unpin" : "Pin this message"}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "2px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          border: "none",
                          background: "transparent",
                          color: "var(--text-muted)",
                          fontSize: "0.62rem",
                          fontWeight: 500,
                          cursor: "pointer",
                          opacity: 0.5,
                          transition: "all 0.15s",
                          marginLeft: "auto",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = "1";
                          e.currentTarget.style.color = "#f59e0b";
                          e.currentTarget.style.background = "rgba(245, 158, 11, 0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = "0.5";
                          e.currentTarget.style.color = "var(--text-muted)";
                          e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <Pin size={10} />
                        {pinnedNotes.some((p) => p.note_id === note.id && p.user_id === userId) ? "Unpin" : "Pin"}
                      </button>
                    </div>

                    {/* Note text with smart rendering */}
                    <div
                      style={{
                        margin: 0,
                        fontSize: "0.82rem",
                        color: "var(--text-secondary)",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {renderSmartContent(note.content, handleMentionClick)}
                    </div>

                    {/* Attachments */}
                    {note.attachments && note.attachments.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        {note.attachments.map((att, i) => {
                          const isImage = att.type.startsWith("image/");
                          return isImage ? (
                            <div
                              key={i}
                              role="button"
                              tabIndex={0}
                              onClick={() => setLightboxImage({ url: att.url, name: att.name })}
                              onKeyDown={(e) => { if (e.key === "Enter") setLightboxImage({ url: att.url, name: att.name }); }}
                              style={{
                                display: "block",
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid var(--border-color-light)",
                                maxWidth: "200px",
                                cursor: "pointer",
                                position: "relative",
                                transition: "transform 0.15s, box-shadow 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.02)";
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "none";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <img
                                src={att.url}
                                alt={att.name}
                                style={{
                                  width: "100%",
                                  maxHeight: "160px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                              {/* Expand hint overlay */}
                              <div style={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: "4px 0",
                                background: "linear-gradient(transparent, rgba(0,0,0,0.5))",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                opacity: 0.7,
                              }}>
                                <ExternalLink size={12} style={{ color: "#fff" }} />
                              </div>
                            </div>
                          ) : (
                            <a
                              key={i}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 12px",
                                borderRadius: "8px",
                                border: "1px solid var(--border-color-light)",
                                background: "var(--bg-surface)",
                                textDecoration: "none",
                                color: "var(--text-secondary)",
                                fontSize: "0.78rem",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "var(--bg-secondary, rgba(0,0,0,0.04))";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "var(--bg-surface)";
                              }}
                            >
                              <FileText size={16} style={{ color: "#6366f1", flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "180px",
                                  }}
                                >
                                  {att.name}
                                </div>
                                <div style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>
                                  {formatFileSize(att.size)}
                                </div>
                              </div>
                              <Download size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Seen-by avatars (Messenger style) */}
                {seenByHere.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: "3px 8px 2px",
                    }}
                  >
                    <div style={{ display: "flex" }}>
                      {seenByHere.map((v, i) => (
                        <div
                          key={v.user_id}
                          className="notes-avatar-tooltip"
                          data-tooltip={`Seen by ${v.user_name}${v.email ? ` (${v.email})` : ""}`}
                          style={{
                            position: "relative",
                            marginLeft: i === 0 ? 0 : "-4px",
                            zIndex: seenByHere.length - i,
                          }}
                        >
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #a5b4fc, #6366f1)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.45rem",
                              fontWeight: 700,
                              border: "1.5px solid var(--bg-surface, #fff)",
                              cursor: "pointer",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                              transition: "transform 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            {getInitials(v.user_name || "U")}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Composer Area */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          borderTop: "1px solid var(--border-color-light)",
          padding: "12px 16px 10px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          background: dragOver
            ? "rgba(99,102,241,0.04)"
            : "var(--bg-surface)",
          transition: "background 0.2s",
          position: "relative",
        }}
      >
        {/* Drag overlay */}
        {dragOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              border: "2px dashed #6366f1",
              borderRadius: "0 0 12px 12px",
              background: "rgba(99,102,241,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <span style={{ color: "#6366f1", fontWeight: 600, fontSize: "0.88rem" }}>
              Drop files here
            </span>
          </div>
        )}

        {/* Mention Dropdown */}
        {showMentions && filteredMentions.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: "100%",
              left: "20px",
              right: "20px",
              maxHeight: "240px",
              overflowY: "auto",
              background: "var(--bg-surface, #fff)",
              border: "1px solid var(--border-color-light)",
              borderRadius: "10px",
              boxShadow: "0 -4px 20px rgba(0,0,0,0.12)",
              zIndex: 50,
              padding: "4px",
            }}
          >
            {/* Group by type */}
            {(["user", "variation", "category", "subcategory", "claim_report"] as const).map((type) => {
              const items = filteredMentions.filter((i) => i.type === type);
              if (items.length === 0) return null;
              const Icon = MENTION_ICONS[type];
              const typeLabel =
                type === "user"
                  ? "Team Members"
                  : type === "variation"
                    ? "Variations"
                    : type === "category"
                      ? "Categories"
                      : type === "subcategory"
                        ? "Subcategories"
                        : "Claim Reports";

              return (
                <div key={type}>
                  <div
                    style={{
                      padding: "6px 10px 3px",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Icon size={10} />
                    {typeLabel}
                  </div>
                  {items.map((item) => {
                    const globalIdx = filteredMentions.indexOf(item);
                    const isActive = globalIdx === mentionIndex;
                    return (
                      <div
                        key={`${item.type}-${item.id}`}
                        onClick={() => handleMentionSelect(item)}
                        onMouseEnter={() => setMentionIndex(globalIdx)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          background: isActive
                            ? "rgba(99,102,241,0.08)"
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          transition: "background 0.1s",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.82rem",
                            fontWeight: isActive ? 600 : 400,
                            color: "var(--text-primary)",
                          }}
                        >
                          {item.label}
                        </span>
                        {item.sublabel && (
                          <span
                            style={{
                              fontSize: "0.7rem",
                              color: "var(--text-muted)",
                            }}
                          >
                            {item.sublabel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Pending file previews */}
        {pendingFiles.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {pendingFiles.map((pf, i) => (
              <div
                key={i}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  border: "1px solid var(--border-color-light)",
                  background: "var(--bg-primary)",
                  fontSize: "0.75rem",
                }}
              >
                {pf.preview ? (
                  <img
                    src={pf.preview}
                    alt=""
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "4px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <FileText size={14} style={{ color: "#6366f1" }} />
                )}
                <span
                  style={{
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pf.file.name}
                </span>
                <button
                  onClick={() => removeFile(i)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px",
                    color: "var(--text-muted)",
                    display: "flex",
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input row: textarea + send button inline (messenger-style) */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: "8px",
          }}
        >
          <div
            ref={editorRef}
            contentEditable
            onInput={handleEditorInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            role="textbox"
            aria-multiline="true"
            aria-label="Write a message"
            data-placeholder="Write a message… Use @ to mention team members, variations & more"
            className="form-input notes-rich-editor"
            style={{
              flex: 1,
              fontSize: "0.85rem",
              minHeight: "52px",
              maxHeight: "140px",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              outline: "none",
              cursor: "text",
            }}
          />
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={(!text.trim() && pendingFiles.length === 0) || submitting}
            aria-label="Send Message"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              padding: "8px 16px",
              fontSize: "0.78rem",
              whiteSpace: "nowrap",
              height: "38px",
              flexShrink: 0,
            }}
          >
            {uploading ? (
              <>
                <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                Uploading…
              </>
            ) : submitting ? (
              <>
                <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                Posting…
              </>
            ) : (
              <>
                <Send size={14} />
                Send
              </>
            )}
          </button>
        </div>

        {/* Action buttons: ghost-style, no border */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach file"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "6px",
              border: "none",
              background: "transparent",
              color: "var(--text-muted)",
              fontSize: "0.75rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(99,102,241,0.06)";
              e.currentTarget.style.color = "#6366f1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            <Paperclip size={13} />
            Attach
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xlsx,.csv"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => {
              const editor = editorRef.current;
              if (!editor) return;
              editor.focus();
              // Insert @ at current cursor position
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                // Add a space before @ if needed
                const textNode = range.startContainer;
                if (textNode.nodeType === Node.TEXT_NODE) {
                  const before = (textNode.textContent || "").slice(0, range.startOffset);
                  if (before.length > 0 && !before.endsWith(" ") && !before.endsWith("\n")) {
                    document.execCommand("insertText", false, " @");
                  } else {
                    document.execCommand("insertText", false, "@");
                  }
                } else {
                  document.execCommand("insertText", false, "@");
                }
              } else {
                document.execCommand("insertText", false, "@");
              }
              // Trigger mention detection
              handleEditorInput();
            }}
            aria-label="Mention"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "6px",
              border: "none",
              background: "transparent",
              color: "var(--text-muted)",
              fontSize: "0.75rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(99,102,241,0.06)";
              e.currentTarget.style.color = "#6366f1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            <AtSign size={13} />
            Mention
          </button>
        </div>
      </div>

      {/* Spin animation keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* ─── Image Lightbox Overlay ─── */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          onKeyDown={(e) => { if (e.key === "Escape") setLightboxImage(null); }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0, 0, 0, 0.85)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            animation: "lightboxFadeIn 0.2s ease",
            cursor: "zoom-out",
          }}
        >
          {/* Top toolbar */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              background: "linear-gradient(rgba(0,0,0,0.6), transparent)",
              zIndex: 2,
            }}
          >
            <span style={{
              color: "rgba(255,255,255,0.85)",
              fontSize: "0.82rem",
              fontWeight: 500,
              maxWidth: "60%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {lightboxImage.name}
            </span>
            <div style={{ display: "flex", gap: "8px" }}>
              <a
                href={lightboxImage.url}
                download={lightboxImage.name}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.85)",
                  cursor: "pointer",
                  transition: "background 0.15s",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
                title="Download"
              >
                <Download size={16} />
              </a>
              <a
                href={lightboxImage.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.85)",
                  cursor: "pointer",
                  transition: "background 0.15s",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
                title="Open in new tab"
              >
                <ExternalLink size={16} />
              </a>
              <button
                onClick={() => setLightboxImage(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.85)",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,0.4)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Image */}
          <img
            src={lightboxImage.url}
            alt={lightboxImage.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
              cursor: "default",
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
